import React, { useState, useEffect } from 'react';
import SelectField from '../common/SelectField.js';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../common/page_component/index.js';
import Alert from '../common/Alert.js';
import vehicleService from '../../services/vehicleService.js';
import vehicleOwnerService from '../../services/VehicleOwnerService.js';
import { Truck, ArrowLeft } from "lucide-react";
import { validators, inputFilters, useValidation, ValidationMessage } from '../common/Validation.js';
import Button from '../common/Button.js';
import VehicleOwnerAssignmentFields, { EMPTY_NEW_OWNER } from './VehicleOwnerAssignmentFields.js';
import VehicleLocationPointFields from './VehicleLocationPointFields.js';
import { mergeCanAccess } from '../../utils/companyFeatures.js';
import { validateLocationPointSelection } from '../../utils/vehicleLocationPoints.js';
import { useAuth } from '../../context/AuthContext';
import { syncVehicleOwnerAssignment, validateVehicleOwnerAssignment } from '../../utils/vehicleOwnerAssign.js';
import { extractVehicleOwnersList } from '../../utils/vehicleOwnerDisplay.js';
import { VEHICLE_TYPE_OPTIONS } from '../../utils/vehicleTypes.js';


export default function AddVehicle() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const companyAccess = mergeCanAccess(user?.company_features || user?.company_can_access || {});
  const hasCommission = Boolean(companyAccess.commission);

  const [loading, setLoading] = useState(false);
  const [vehicleOwners, setVehicleOwners] = useState([]);
  const [ownerMode, setOwnerMode] = useState('none');
  const [selectedOwnerId, setSelectedOwnerId] = useState('');
  const [newOwnerData, setNewOwnerData] = useState({ ...EMPTY_NEW_OWNER });
  const [ownerPercentageOverride, setOwnerPercentageOverride] = useState('');
  const [pickupLocationIds, setPickupLocationIds] = useState([]);
  const [dropLocationIds, setDropLocationIds] = useState([]);
  const [formData, setFormData] = useState({
    vehicle_name: '',
    vehicle_number: '',
    vehicle_type: 'cargo_goods',
    status: 'active',
    vehicle_size: '',
    capacity: '',
    vehicle_contract_amount: ''
  });

  const { errors, validate, validateField } = useValidation();

  const validationRules = {
    vehicle_name:            () => '',
    vehicle_number:          (v) => validators.required(v, 'Vehicle number'),
    vehicle_size:            (v) => v ? validators.number(v, { min: 0, label: 'Vehicle size' }) : '',
    capacity:                (v) => v ? validators.number(v, { min: 0, label: 'Capacity' }) : '',
    vehicle_contract_amount: (v) => validators.number(v, { min: 0, label: 'Contract amount' }),
  };

  const vehicleTypes = VEHICLE_TYPE_OPTIONS;

  const vehicleStatuses = [
    { value: 'active', label: 'ACTIVE' },
    { value: 'inactive', label: 'INACTIVE' }
  ];

  useEffect(() => {
    vehicleOwnerService
      .getAll(1, 500)
      .then((res) => setVehicleOwners(extractVehicleOwnersList(res)))
      .catch(() => setVehicleOwners([]));
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;

    let filtered = value;
    if (name === 'vehicle_contract_amount' || name === 'vehicle_size' || name === 'capacity') filtered = inputFilters.amount(value);
    else if (name === 'vehicle_number') filtered = value.toUpperCase();

    setFormData(prev => ({ ...prev, [name]: filtered }));

    if (validationRules[name]) {
      validateField(name, filtered, validationRules[name]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate(formData, validationRules)) {
      Alert.error('Please fix the highlighted errors before submitting');
      return;
    }

    const ownerError = validateVehicleOwnerAssignment({
      ownerMode,
      selectedOwnerId,
      newOwnerData,
      ownerPercentageOverride,
      vehicleOwners,
      hasCommission,
    });
    if (ownerError) {
      Alert.error(ownerError);
      return;
    }

    const locationError = validateLocationPointSelection(pickupLocationIds, dropLocationIds);
    if (locationError) {
      Alert.error(locationError);
      return;
    }

    try {
      setLoading(true);

      const formDataToSend = new FormData();
      Object.keys(formData).forEach(key => {
        if (formData[key] !== null && formData[key] !== undefined && formData[key] !== '') {
          formDataToSend.append(key, formData[key]);
        }
      });

      formDataToSend.append(
        'pickup_point',
        JSON.stringify(pickupLocationIds.map((locationId) => ({ location_id: locationId })))
      );
      formDataToSend.append(
        'drop_point',
        JSON.stringify(dropLocationIds.map((locationId) => ({ location_id: locationId })))
      );

      const response = await vehicleService.createVehicle(formDataToSend);
      const created = response.data?.data || response.data;
      const newVehicleId = created?.id || created?.vehicle_id;

      if (newVehicleId && ownerMode !== 'none') {
        await syncVehicleOwnerAssignment({
          vehicleId: newVehicleId,
          ownerMode,
          selectedOwnerId,
          vehicleOwners,
          newOwnerData,
          ownerPercentageOverride,
        });
      }

      Alert.success('Vehicle created successfully');
      setTimeout(() => navigate('/vehicles'), 1000);
    } catch (error) {
      Alert.error(error.response?.data?.error || 'Failed to create vehicle');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    const hasData = formData.vehicle_name || formData.vehicle_number ||
                    formData.vehicle_size || formData.capacity;

    if (hasData) {
      const confirmed = await Alert.confirm(
        'You have unsaved changes. Are you sure you want to cancel?',
        'Unsaved Changes'
      );
      if (!confirmed) return;
    }

    navigate('/vehicles');
  };

  return (
    <DashboardLayout>
      <div className="w-full min-w-0 max-w-full mx-auto">
        {/* Header Section */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-4 mb-4 sm:mb-6">
          <h1 className="text-2xl sm:text-xl font-bold text-secondary flex items-center gap-2 sm:gap-3">
            <Truck className="w-6 h-6 text-secondary shrink-0" />Add New Vehicle
          </h1>
          <button
            onClick={handleCancel}
            className="group flex items-center justify-center gap-2 px-3 sm:px-3 py-2.5 sm:py-2 text-xs sm:text-sm text-buttonBg bg-buttonTxt hover:text-buttonTxt hover:bg-buttonBg border border-current rounded-xl transition-all duration-300 font-medium whitespace-nowrap shrink-0"
          >
            <span className="group-hover:-translate-x-1 transition-transform"><ArrowLeft className="w-4 h-4" /></span>
            Back to Vehicles
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm p-4 sm:p-8 space-y-6 sm:space-y-8">
          {/* Vehicle Information */}
          <div>
            <h3 className="text-base sm:text-md font-semibold text-gray-700 mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
              Vehicle Information
            </h3>
            <div className='grid grid-cols-2 md:grid-cols-2 xl:grid-cols-4 gap-2 sm:gap-6'>
              {/* Vehicle Type */}
              <div>
                <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                  Vehicle Type
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <SelectField variant="form"
                  name="vehicle_type"
                  value={formData.vehicle_type}
                  onChange={handleChange}
                  className="w-full px-3 sm:px-4 py-2 border border-gray-300 text-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base bg-white"
                >
                  {vehicleTypes.map((type) => (
                    <option key={type.value} value={type.value} className="text-gray-700">
                      {type.label}
                    </option>
                  ))}
                </SelectField>
              </div>

              {/* Vehicle Number */}
              <div>
                <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                  Vehicle Number
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <input
                  type="text"
                  name="vehicle_number"
                  value={formData.vehicle_number}
                  onChange={handleChange}
                  placeholder="Enter vehicle number (e.g., MH12AB1234)"
                  required
                  className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.vehicle_number ? 'border-red-400' : 'border-gray-300'}`}
                />
                <ValidationMessage error={errors.vehicle_number} />
              </div>

              {/* Vehicle Name */}
              <div>
                <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                  Vehicle Name
                </label>
                <input
                  type="text"
                  name="vehicle_name"
                  value={formData.vehicle_name}
                  onChange={handleChange}
                  placeholder="Enter vehicle name (e.g., Tata Ace)"
                  className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.vehicle_name ? 'border-red-400' : 'border-gray-300'}`}
                />
                <ValidationMessage error={errors.vehicle_name} />
              </div>

              {/* Vehicle Size (Ft) */}
              <div>
                <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                  Vehicle Size (Ft)
                </label>
                <input
                  type="text"
                  name="vehicle_size"
                  value={formData.vehicle_size}
                  onChange={handleChange}
                  placeholder="e.g., 20"
                  className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.vehicle_size ? 'border-red-400' : 'border-gray-300'}`}
                />
                <ValidationMessage error={errors.vehicle_size} />
              </div>

              {/* Capacity (MT) */}
              <div>
                <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                  Capacity (MT)
                </label>
                <input
                  type="text"
                  name="capacity"
                  value={formData.capacity}
                  onChange={handleChange}
                  placeholder="e.g., 9"
                  className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.capacity ? 'border-red-400' : 'border-gray-300'}`}
                />
                <ValidationMessage error={errors.capacity} />
                <p className="text-xs text-gray-500 mt-1">Used to warn about overweight loads during ML creation.</p>
              </div>

              {/* Status */}
              <div>
                <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                  Status
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <SelectField variant="form"
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="w-full px-3 text-gray-700 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                >
                  {vehicleStatuses.map(status => (
                    <option key={status.value} value={status.value}>
                      {status.label}
                    </option>
                  ))}
                </SelectField>
              </div>

              {/* Vehicle Contract Amount */}
              <div>
                <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                  Contract Amount (₹)
                </label>
                <input
                  type="text"
                  name="vehicle_contract_amount"
                  value={formData.vehicle_contract_amount}
                  onChange={handleChange}
                  placeholder="Enter contract amount"
                  className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.vehicle_contract_amount ? 'border-red-400' : 'border-gray-300'}`}
                />
                <ValidationMessage error={errors.vehicle_contract_amount} />
              </div>
            </div>

            <VehicleLocationPointFields
              pickupLocationIds={pickupLocationIds}
              setPickupLocationIds={setPickupLocationIds}
              dropLocationIds={dropLocationIds}
              setDropLocationIds={setDropLocationIds}
            />

            <VehicleOwnerAssignmentFields
              ownerMode={ownerMode}
              setOwnerMode={setOwnerMode}
              selectedOwnerId={selectedOwnerId}
              setSelectedOwnerId={setSelectedOwnerId}
              newOwnerData={newOwnerData}
              setNewOwnerData={setNewOwnerData}
              ownerPercentageOverride={ownerPercentageOverride}
              setOwnerPercentageOverride={setOwnerPercentageOverride}
            />
          </div>

          {/* Form Actions */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Button variant="primary" type="submit" disabled={loading} loading={loading}>
              {loading ? 'Creating Vehicle...' : 'Create Vehicle'}
            </Button>
            <Button variant="secondary" onClick={handleCancel}>
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
}
